import React, { Fragment, useContext, useState } from 'react';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import ClassicLoadingSpinner from './LoadingSpinner/ClassicLoadingSpinner';
import i18n, { formatNumber, getDateFormatOptions } from '@/utils/i18n';
import { FaEdit, FaExchangeAlt, FaImages, FaRedo } from 'react-icons/fa';
import ItemLocationSearch from './ItemLocationSearch';
import { useMoveItemToItemLocationMutation } from '../graphql/mutations/changeItemLocation.generated';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CustomTable } from './Table';
import { Link } from 'react-router-dom';
import { ContentSection } from './Layout/ContentSection';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import CleanOrDirtyText from './CleanOrDirtyText';
import ContainerTypeDisplay from './ContainerType/ContainerTypeDisplay';
import ContainerStationStatusBadge from './ContainerStation/ContainerStationStatusBadge';
import OrderfulfillmentPicturesGallery from '../views/Events/OrderFulfillment/Components/OrderfulfillmentPicturesGallery';
import { ViewerContext } from '../providers/ViewerContextProvider';
import TrackableItemStatusBadge from './TrackableItem/TrackableItemStatusBadge';
import { useGetItemLocationHistoryPaginatedQuery } from '../graphql/queries/getItemLocationHistoryPaginated.generated';
import TrackableItemDisplay from './TrackableItem/TrackableItemDisplay';
import { TrackableItem } from '../graphql/generatedTypes';
import { Role } from '../contants';

const ITEM_LOCATION_HISTORY_PAGE_SIZE = 20;

type ItemLocationHistoryProps = {
  itemId: string;
};

const ItemLocationHistory = ({ itemId }: ItemLocationHistoryProps) => {
  const context = useContext(ViewerContext);
  const readOnly = context.role === Role.eventCleaningEmployee;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [showCSPicturesModal, setShowCSPicturesModal] = useState(false);
  const [containerStationIdForPictures, setContainerStationIdForPictures] =
    useState<string | null>();

  const { data, loading, refetch, error } =
    useGetItemLocationHistoryPaginatedQuery({
      skip: isNullOrWhitespace(itemId),
      variables: {
        item_id: itemId,
        skip: 0,
        take: ITEM_LOCATION_HISTORY_PAGE_SIZE,
      },
    });

  const [moveItemToLocation, { loading: moving }] =
    useMoveItemToItemLocationMutation({
      onCompleted: (d) => {
        setIsModalOpen(false);
        refetch();
      },
    });

  if (loading) {
    return <ClassicLoadingSpinner />;
  }

  const history = data?.getItemLocationHistoryPaginated?.items;
  const isTrackableItem = data?.getItemLocationHistoryPaginated?.items.some(
    (i) => {
      return i.item_type?.category === 'ASSET';
    },
  );

  const tableHeaders = [
    i18n.t('ItemLocation:status'),
    i18n.t('ItemLocationHistory:locationName'),
    i18n.t('Common:event'),
    i18n.t('ItemLocationHistory:arrivalDate'),
    !isTrackableItem ? i18n.t('Common:containerType') : i18n.t('Common:type'),
    !isTrackableItem ? i18n.t('Common:amount') : undefined,
    !isTrackableItem ? i18n.t('ItemLocation:containerStatus') : undefined,
    i18n.t('ItemLocationHistory:bookedBy'),
    i18n.t('ItemLocationHistory:orderFulfillment'),
    !isTrackableItem ? i18n.t('Common:pictures') : undefined,
  ].filter((h): h is string => {
    return h !== undefined;
  });

  if (error || (history == null && !loading)) {
    console.log(`Error loading history: ${error}`);
    return <p>{i18n.t('Common:error')}</p>;
  }

  return (
    <ContentSection
      title={i18n.t('ItemLocationHistory:itemLocationHistory')}
      collapsable={false}
    >
      <div className="float-end mb-2">
        <Button
          className="ms-1 mb-1"
          disabled={loading}
          onClick={() => {
            refetch();
          }}
        >
          <FaRedo />
        </Button>
        {!readOnly && (
          <Button
            className="ms-1 mb-1"
            disabled={moving}
            onClick={() => setIsModalOpen(true)}
          >
            <FaExchangeAlt className="me-1" />{' '}
            {i18n.t('ItemLocationHistory:changeLocation')}
          </Button>
        )}
      </div>

      <CustomTable
        error={error}
        hover
        striped
        bordered
        headers={tableHeaders}
        loading={loading}
        rows={(history || []).map((entry) => {
          const hasContainerTypeAmounts =
            entry.container_type_amounts.length > 0;

          const rowSpan = Math.max(entry.container_type_amounts.length, 1);

          return (
            <Fragment key={entry.id}>
              {!hasContainerTypeAmounts && (
                <tr key={`${entry.id}`}>
                  <td>
                    {isTrackableItem && (
                      <TrackableItemStatusBadge
                        archived={entry.item_type?.archived || false}
                      />
                    )}
                    {!isTrackableItem && (
                      <ContainerStationStatusBadge status={entry.status} />
                    )}
                  </td>

                  <td>
                    <a href={`/item-location/${entry.current_location_id}`}>
                      {entry.current_location_name}
                    </a>
                  </td>

                  <td>
                    {!entry.event?.id && '-'}
                    {entry.event?.id && (
                      <Link to={'/event/' + entry.event?.id}>
                        {entry.event?.name || entry.event?.id || ''}
                      </Link>
                    )}
                  </td>

                  <td>
                    {new Date(entry.arrival_date).toLocaleDateString(
                      i18n.language,
                      getDateFormatOptions(true),
                    )}
                  </td>

                  {isTrackableItem && (
                    <td>
                      <TrackableItemDisplay
                        trackableItem={
                          {
                            id: entry.item_type?.id || '-',
                            name: entry.item_type?.name,
                            item_type: entry.item_type,
                          } as TrackableItem
                        }
                        showTrackableItemId={false}
                        showName={true}
                        showLink={false}
                        size={'s'}
                      />
                    </td>
                  )}
                  {!isTrackableItem && <td>-</td>}
                  {!isTrackableItem && <td className="numbers-formatted">-</td>}
                  {!isTrackableItem && <td>-</td>}

                  <td>
                    {entry.booked_by_id != null ? (
                      <Link to={`/users/${entry.booked_by_id}`}>
                        {entry.booked_by}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>

                  <td>
                    {entry.order_fulfillment_id != null ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                        }}
                      >
                        <Link
                          to={`/order-fulfillment/${entry.order_fulfillment_id}`}
                        >
                          <Button>
                            <FaEdit />
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>

                  {!isTrackableItem && (
                    <td>
                      {entry.pictures.length === 0 && '-'}
                      {entry.pictures.length > 0 && (
                        <Button
                          onClick={() => {
                            setContainerStationIdForPictures(entry.id);
                            setShowCSPicturesModal(true);
                          }}
                        >
                          <FaImages />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              )}

              {hasContainerTypeAmounts &&
                entry.container_type_amounts.map((cta, index) => {
                  const isFirstRow = index === 0;

                  const hasAmount = cta.amount > cta.amount_sealed;
                  const hasAmountSealed = cta.amount_sealed > cta.amount;
                  const bothZero = cta.amount === 0 && cta.amount_sealed === 0;

                  return (
                    <tr key={`${entry.id}${index}`}>
                      {isFirstRow && (
                        <td rowSpan={rowSpan}>
                          <ContainerStationStatusBadge status={entry.status} />
                        </td>
                      )}

                      {isFirstRow && (
                        <td rowSpan={rowSpan}>
                          <a
                            href={`/item-location/${entry.current_location_id}`}
                          >
                            {entry.current_location_name}
                          </a>
                        </td>
                      )}

                      {isFirstRow && (
                        <td rowSpan={rowSpan}>
                          {!entry.event?.id && '-'}
                          {entry.event?.id && (
                            <Link to={'/event/' + entry.event?.id}>
                              {entry.event?.name || entry.event?.id || ''}
                            </Link>
                          )}
                        </td>
                      )}

                      {isFirstRow && (
                        <td rowSpan={rowSpan}>
                          {new Date(entry.arrival_date).toLocaleDateString(
                            i18n.language,
                            getDateFormatOptions(true),
                          )}
                        </td>
                      )}

                      <td>
                        {hasContainerTypeAmounts && (
                          <ContainerTypeDisplay
                            containerType={{
                              id: cta.container_type_id,
                              imageUrl:
                                cta.container_type_image ||
                                DEFAULT_CONTAINER_TYPE_ICON,
                              name: cta.container_type_name,
                              hexCode: '',
                            }}
                            showContainerId={false}
                            size="s"
                          />
                        )}
                      </td>

                      <td className="numbers-formatted">
                        {hasContainerTypeAmounts && (
                          <>
                            {bothZero && '-'}
                            {hasAmount && formatNumber(cta.amount)}
                            {hasAmountSealed && formatNumber(cta.amount_sealed)}
                          </>
                        )}
                      </td>

                      {!isTrackableItem && (
                        <td>
                          {hasContainerTypeAmounts && (
                            <>
                              {bothZero && '-'}
                              {hasAmount && <CleanOrDirtyText isDirty />}
                              {hasAmountSealed && <CleanOrDirtyText isClean />}
                            </>
                          )}
                        </td>
                      )}

                      {isFirstRow && (
                        <td rowSpan={rowSpan}>
                          {entry.booked_by_id != null ? (
                            <Link to={`/users/${entry.booked_by_id}`}>
                              {entry.booked_by}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                      )}

                      {isFirstRow && (
                        <td rowSpan={rowSpan}>
                          {entry.order_fulfillment_id != null ? (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                              }}
                            >
                              <Link
                                to={`/order-fulfillment/${entry.order_fulfillment_id}`}
                              >
                                <Button>
                                  <FaEdit />
                                </Button>
                              </Link>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                      )}

                      {isFirstRow && !isTrackableItem && (
                        <td rowSpan={rowSpan}>
                          {entry.pictures.length === 0 && '-'}
                          {entry.pictures.length > 0 && (
                            <Button
                              onClick={() => {
                                setContainerStationIdForPictures(entry.id);
                                setShowCSPicturesModal(true);
                              }}
                            >
                              <FaImages />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </Fragment>
          );
        })}
      />

      {showCSPicturesModal && (
        <OrderfulfillmentPicturesGallery
          pictures={
            history?.find((station) => {
              return station.id === containerStationIdForPictures;
            })?.pictures
          }
          onClose={() => {
            setShowCSPicturesModal(false);
          }}
        />
      )}

      <Modal isOpen={isModalOpen} onClosed={() => setIsModalOpen(false)}>
        <ModalHeader toggle={() => setIsModalOpen(false)}>
          {i18n.t('ItemLocationHistory:moveModalTitle')}
        </ModalHeader>
        <ModalBody>
          <ItemLocationSearch
            setLocationInfo={(id) => setSelectedLocationId(id)}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            className="float-end ms-1"
            color="danger"
            onClick={() => setIsModalOpen(false)}
          >
            {i18n.t('Common:cancel')}
          </Button>
          <Button
            className="float-end ms-1"
            disabled={isNullOrWhitespace(selectedLocationId)}
            onClick={() => {
              moveItemToLocation({
                variables: {
                  item_id: itemId,
                  to_location_id: selectedLocationId,
                },
              });
              setIsModalOpen(false);
              setSelectedLocationId('');
            }}
          >
            {i18n.t('ItemLocationHistory:move')}
          </Button>
        </ModalFooter>
      </Modal>
    </ContentSection>
  );
};

export default ItemLocationHistory;
